import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from 'react';
import { Box, Grid, } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useStore } from '@mcn-platform/models';
import Content from './MicrocastContent';
import Notes from '../class/notes/Notes';
import MicrocastBar from './MicrocastBar';
import KickerWidget from '../kicker/KickerWidget';
import formatSeconds from '../common/formatSeconds';
import ScormFrame from '../common/scorm/ScormPlayer';
import PlaceHolder from '../common/PlaceHolder';
import NotAuthorizedBanner from '../common/NotAuthorizedBanner';
const Microcast = ({ microcastID, settings: { showNotes, showTranscripts, showKicker, showCTA, showOverview, showQnATab, }, editData, }) => {
    const [microcast, setMicrocast] = useState();
    const [notAuthorized, setNotAuthorized] = useState(false);
    const [itemProgress, setItemProgress] = useState({});
    const [currentPosition, setCurrentPosition] = useState(0);
    const [lastPercentUpdate, setLastPercentUpdate] = useState(0);
    const [lessonDuration, setLessonDuration] = useState(0);
    const [maxPosition, setMaxPosition] = useState(0);
    const [seekPosition, setSeekPosition] = useState(null);
    const [seekTrigger, setSeekTrigger] = useState(null);
    const [kicker, setKicker] = useState();
    const store = useStore();
    const { currentPortal, isAnonymous } = store;
    const updateProgressInterval = 10;
    const updateProgress = async (progress, lastPosition, newMaxPosition) => {
        setCurrentPosition(lastPosition);
        setMaxPosition(newMaxPosition);
        setItemProgress(await store.trackMicrocastProgress(microcast?.key, progress, lastPosition, maxPosition));
    };
    useEffect(() => {
        const startMicrocast = async () => {
            if (microcastID) {
                const newMicrocast = await store.load('microcast', microcastID);
                setMicrocast(newMicrocast);
                const newItemProgress = await store.startMicrocast(newMicrocast);
                setItemProgress(newItemProgress);
                if (!newItemProgress) {
                    console.log('didnt get a progress - no access to take this microcast');
                    setNotAuthorized(true);
                    return;
                }
                setNotAuthorized(false);
                setSeekPosition(newItemProgress.lastPosition || 0);
                setMaxPosition(newItemProgress.maxPosition || 0);
                console.log('startMicrocast complete', newItemProgress);
            }
            else {
                console.log('No microcast specified', microcastID);
            }
        };
        console.log('Starting microcast', microcastID);
        startMicrocast();
    }, [store, microcastID]);
    useEffect(() => {
        if (microcast?.kicker?.id) {
            setKicker(microcast?.kicker);
        }
    }, [microcast?.kicker]);
    const playerContainerRef = React.useRef(null);
    const handleViewNote = (note) => {
        const { reference } = note;
        const courseItemDetails = reference?.split('/') || [];
        setSeekPosition(parseInt(courseItemDetails[3], 10));
        setSeekTrigger(Date.now());
        // @ts-expect-error Fix the type on the ref and this should work
        playerContainerRef.current.scrollIntoView({ block: 'start', behavior: 'smooth' });
    };
    const OnMediaProgress = (newCurrentPosition, newMaxPosition) => {
        if (!microcast || !itemProgress) {
            return;
        }
        if (microcast.format === 'standard') {
            const percent = Math.ceil((currentPosition / lessonDuration) * 100);
            if (newCurrentPosition > 0 && Math.floor(newCurrentPosition) % updateProgressInterval === 0) {
                updateProgress(percent, newCurrentPosition, newMaxPosition);
            }
            else if ((percent >= 99 || (lessonDuration - newCurrentPosition) < 5)
                && percent !== lastPercentUpdate) {
                // flip it to done if over 99% or less than 5 seconds remaining
                updateProgress(100, newCurrentPosition, newMaxPosition);
                setLastPercentUpdate(percent);
            }
        }
        if (setCurrentPosition) {
            setCurrentPosition(Math.floor(newCurrentPosition));
        }
    };
    const OnComplete = () => {
        if (updateProgress) {
            updateProgress(100, lessonDuration, lessonDuration);
        }
    };
    // it can be an array of id/name pairs
    const makeReference = () => ([
        `${microcast?.id}/${microcast?.id}/video/${currentPosition}`,
        `${microcast?.name}/${microcast?.name}/Video/${formatSeconds(currentPosition)}`,
    ]);
    if (editData && !microcast) {
        return _jsx(PlaceHolder, { placeHolderName: "Select microcast to see preview" });
    }
    if (microcast?.syncState !== 'loaded') {
        return _jsx("div", { children: "\u00A0" });
    }
    if (microcast.format === 'scorm12') {
        console.log('Showing SCORM12 microcast');
        if (notAuthorized) {
            return _jsx(NotAuthorizedBanner, { currentPortal: currentPortal });
        }
        return (_jsx(ScormFrame, { itemType: "microcast", item: microcast, itemProgress: itemProgress }));
    }
    return (_jsx(Box, { children: _jsxs(Grid, { container: true, children: [_jsx(MicrocastBar, { name: microcast.name }), _jsx(Grid, { item: true, xs: 12, md: 12, order: { xs: 1, md: 0 }, ref: playerContainerRef, children: _jsx(Grid, { item: true, xs: 12, md: 12, order: { xs: 0, md: 1 }, sx: { p: 2 }, children: _jsxs(Grid, { container: true, spacing: 2, children: [_jsx(Grid, { item: true, xs: 12, md: 9, children: notAuthorized ? (_jsx(NotAuthorizedBanner, { currentPortal: currentPortal })) : (
                                    // eslint-disable-next-line react/jsx-no-useless-fragment
                                    _jsx(Content, { item: microcast, onDuration: setLessonDuration, OnMediaProgress: OnMediaProgress, OnComplete: OnComplete, seekPosition: seekPosition, seekTrigger: seekTrigger, showTranscripts: showTranscripts, showOverview: showOverview, showCTA: showCTA, showMakeANoteBtn: showNotes, showQnATab: showQnATab })) }), _jsxs(Grid, { item: true, xs: 12, md: 3, children: [showKicker && (_jsx(Box, { sx: { mt: 2 }, children: _jsx(KickerWidget, { kickerID: kicker?.id, limitToPageWidth: true }) })), !isAnonymous && showNotes && (_jsx(Notes, { handleViewNote: handleViewNote, makeReference: makeReference, isCourseItemSelected: !!microcast?.id, progress: itemProgress, modelName: "microcastProgress" }))] })] }) }) })] }) }));
};
Microcast.defaultProps = {
    settings: {
        showNotes: true,
        showTranscripts: true,
        showKicker: true,
        showCTA: true,
        showOverview: true,
        showQnATab: true,
    },
    editData: undefined,
};
Microcast.meta = {
    group: 'deliveryPages',
    name: 'Microcast Delivery Panel',
    description: 'Displays the full microcast delivery interface.',
    props: [
        {
            key: 'microcastID',
            returnId: true, // hack to to make the picker return the course ID
            name: 'microcast',
            type: 'microcast', // note this is a special type recognized by the system
            description: 'The microcast being delivered.',
        },
        {
            key: 'settings.showTranscripts',
            name: 'Show Transcripts',
            type: 'boolean',
            description: 'Show the intro video on the card',
        },
        {
            key: 'settings.showNotes',
            name: 'Show Notes',
            type: 'boolean',
            description: 'Show the course info on the card',
        },
        {
            key: 'settings.showKicker',
            name: 'Show Kicker',
            type: 'boolean',
            description: 'Show the course headline on the card',
        },
        {
            key: 'settings.showCTA',
            name: 'Show CTA',
            type: 'boolean',
            description: 'Show the course description on the card',
        },
        {
            key: 'settings.showOverview',
            name: 'Show Overview',
            type: 'boolean',
            description: 'Show the order by company name on the card',
        },
        {
            key: 'settings.showQnATab',
            name: 'Show Q&A Tab',
            type: 'boolean',
            description: 'Show the Q&A tab.',
        },
    ],
    defaultConfig: {
        type: 'component',
        component: 'Microcast',
        props: {
            microcastID: '@microcastID',
            settings: {
                showNotes: true,
                showTranscripts: true,
                showKicker: true,
                showCTA: true,
                showOverview: true,
                showQnATab: true,
            },
        },
    },
};
export default observer(Microcast);
//# sourceMappingURL=Microcast.js.map